import { useState } from 'react';

import api from 'api';
import { EOrganization, ESnapshot } from 'lib/types';
import { MAX_PUBLISHER_INVOICE_DOWNLOADS } from 'lib/types/publisherInvoices';
import { TableLayout } from 'lib/components/TableLayout';
import { FinancePaper } from 'lib/components/gifs';
import { columnObjectsAreEqual } from 'lib/utils/stringify';
import { ColumnService } from 'lib/services/directory';
import { logAndCaptureException } from 'utils';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { useAppDispatch } from 'redux/hooks';
import ToastActions from 'redux/toast';
import { PublisherInvoicesTableRow } from './PublisherInvoicesTableRow';
import PublisherInvoicesFilterDialog from './PublisherInvoicesFilterDialog';
import {
  DEFAULT_PUBLISHER_INVOICE_FILTER,
  getNumActivePublisherInvoiceFilters,
  publisherInvoiceMatchesFilter
} from './publisherInvoiceTableSettingsUtils';

const PAGE_SIZE = 10;

type PublisherInvoicesTableProps = {
  organization: ESnapshot<EOrganization> | null;
};

const columns = [
  <th key="name" className="font-medium w-56">
    Name
  </th>,
  <th key="date-created" className="font-medium w-56">
    Date Created
  </th>,
  <th key="amount" className="font-medium w-56">
    Amount
  </th>,
  <th key="status" className="font-medium w-56">
    Status
  </th>,
  <th key="due-date" className="font-medium w-56">
    Due Date
  </th>,
  <th key="actions" className="font-medium w-40">
    Actions
  </th>
];

/**
 * Table of the Column invoices (Stripe subscription invoices) issued to the
 * active organization.
 */
export default function PublisherInvoicesTable({
  organization
}: PublisherInvoicesTableProps) {
  const dispatch = useAppDispatch();
  const [invoiceFilter, setInvoiceFilter] = useState(
    DEFAULT_PUBLISHER_INVOICE_FILTER
  );
  const [updatedInvoiceFilter, setUpdatedInvoiceFilter] =
    useState(invoiceFilter);

  // columnObjectsAreEqual does not work with comparing date ranges
  // of this type, so we have to do a separate comparison
  const filterHasChanges =
    !columnObjectsAreEqual(updatedInvoiceFilter, invoiceFilter) ||
    updatedInvoiceFilter.dateRange !== invoiceFilter.dateRange;

  const { value, isLoading } = useAsyncEffect({
    fetchData: async () => {
      const { response, error } = await api.safePost(
        'payments/publisher-invoices',
        {}
      );
      if (error) {
        logAndCaptureException(
          ColumnService.PAYMENTS,
          error,
          'Failed to load publisher invoices',
          { organizationId: organization?.id ?? '' }
        );
        return null;
      }
      return response;
    },
    dependencies: [organization?.id]
  });

  const invoices = value?.invoices;
  const title = `Total invoices: ${invoices?.length ?? 0}${
    value?.truncated ? '+' : ''
  }`;

  /**
   * Downloads a zip holding one folder per invoice (Stripe PDF + detail CSVs)
   * covering only the invoices matching the committed filter — the same
   * predicate the table body renders with.
   */
  const handleDownloadFilteredInvoices = async () => {
    const filteredInvoices = (invoices ?? []).filter(invoice =>
      publisherInvoiceMatchesFilter(invoice, invoiceFilter)
    );
    if (!filteredInvoices.length) {
      dispatch(
        ToastActions.toastError({
          headerText: 'Failed to download invoices',
          bodyText: 'There are no downloadable invoices matching your filters.'
        })
      );
      return;
    }
    if (filteredInvoices.length > MAX_PUBLISHER_INVOICE_DOWNLOADS) {
      dispatch(
        ToastActions.toastError({
          headerText: 'Too many invoices selected',
          bodyText: `Downloads are limited to ${MAX_PUBLISHER_INVOICE_DOWNLOADS} invoices at a time. Please apply filters to narrow the results.`
        })
      );
      return;
    }

    const { response, error } = await api.safePost(
      'payments/publisher-invoices/download',
      { stripeInvoiceIds: filteredInvoices.map(invoice => invoice.id) }
    );
    if (error || !response) {
      logAndCaptureException(
        ColumnService.PAYMENTS,
        error,
        'Failed to download publisher invoices',
        { organizationId: organization?.id ?? '' }
      );
      dispatch(
        ToastActions.toastError({
          headerText: 'Failed to download invoices',
          bodyText:
            'Our team has been notified and we will look into the issue immediately. Please try again later.'
        })
      );
      return;
    }

    window.open(response.url, '_blank');
    dispatch(
      ToastActions.toastSuccess({
        headerText: 'Invoices downloaded',
        bodyText:
          "Please check your download folder to view the invoices. If you don't see the file, make sure that you have downloads enabled for column.us."
      })
    );
  };

  return (
    <TableLayout
      header={{
        subtitle: 'Track invoices from Column for your organization.',
        title
      }}
      columns={columns}
      renderRow={invoice => <PublisherInvoicesTableRow invoice={invoice} />}
      noResultsContent={
        invoices?.length === 0
          ? {
              header: 'No invoices yet',
              subheader:
                'Invoices from Column will appear here once they are issued.',
              icon: <img src={FinancePaper} />
            }
          : undefined
      }
      data={invoices || []}
      loading={isLoading}
      id="publisher-invoices"
      downloadable={{
        buttonText: 'Download Invoices',
        onClickDownload: handleDownloadFilteredInvoices
      }}
      filterable={{
        searchEnabled: false,
        shouldShowTableItem: invoice =>
          publisherInvoiceMatchesFilter(invoice, invoiceFilter),
        additionalFilters: {
          applyFilterChanges: () => setInvoiceFilter(updatedInvoiceFilter),
          resetFilters: () => {
            setUpdatedInvoiceFilter(DEFAULT_PUBLISHER_INVOICE_FILTER);
            setInvoiceFilter(DEFAULT_PUBLISHER_INVOICE_FILTER);
          },
          filterHasChanges,
          numFiltersActive: getNumActivePublisherInvoiceFilters(invoiceFilter),
          renderDialog: () => (
            <PublisherInvoicesFilterDialog
              setUpdatedFilter={setUpdatedInvoiceFilter}
              updatedFilter={updatedInvoiceFilter}
            />
          )
        }
      }}
      pagination={{ pageSize: PAGE_SIZE }}
    />
  );
}
